import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { collection, getDocs, orderBy, query, limit, startAfter, doc, getDoc } from "firebase/firestore";
import { db } from "../../utils/firebase/firebase.utils.js";
import { selectUser } from "../../store/user/user.selector.js";

// styles
import {
  ActivityLogContainer,
  ActivityLogDisplay,
  FilterContainer
} from "./ActivityLog.styles.js";

// components
import AccountUpdatedEntry from "./components/account-updated-entry/AccountUpdatedEntry.js";
import CollectionUpdatedEntry from "./components/collection-updated-entry/CollectionUpdatedEntry.js";
import InputDropdown from '../../components/input-dropdown/InputDropdown.js';
import TeamUpdatedEntry from "./components/team-updated-entry/TeamUpdatedEntry.js";
import TokenPurchaseEntry from "./components/token-purchase-entry/TokenPurchaseEntry.js";
import Title from '../../components/widgets/components/title/Title.js';
import TokenRedemptionEntry from "./components/token-redemption-entry/TokenRedemptionEntry.js";
import SystemPaginationControls from '../../components/system/pagination-controls/SystemPaginationControls.js';

// Define activity type groups outside component to avoid recreation on every render
const ACTIVITY_TYPE_GROUPS = {
  all: null, // null means no filtering
  accountUpdated: ["updatedTeam", "updatedAccount"],
  addedToCollection: ["addedToCollection"],
  removedFromCollection: ["removedFromCollection"],
  sales: ["soldFromCollection", "returnedToCollection"],
  tokenPurchase: ["tokenPurchase"],
  tokenRedemption: ["checklistUnlocked", "promoCodeRedeemed", "welcomeTokensGranted"],
  tokenActivities: ["tokenPurchase", "checklistUnlocked", "promoCodeRedeemed", "welcomeTokensGranted"]
};

const COLLECTION_CARD_ACTION_TYPES = [
  'addedToCollection',
  'removedFromCollection',
  'soldFromCollection',
  'returnedToCollection'
];

export default function ActivityLog({ tokenOnly = false }) {
  const user = useSelector(selectUser);
  const [activityLog, setActivityLog] = useState([]);
  const [filteredType, setFilteredType] = useState(tokenOnly ? "tokenActivities" : "all");
  const [filteredSeries, setFilteredSeries] = useState("all");
  const [unlockedSeries, setUnlockedSeries] = useState([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [, setPageCache] = useState({}); // Cache for each page
  const pageCacheRef = useRef({}); // Ref to avoid dependency issues
  const ITEMS_PER_PAGE = 10;

  // Filter activityLog based on actionType groups and series
  const filteredActivityLog = useMemo(() => {

    let filtered = activityLog;

    // First filter by activity type
    if (filteredType !== "all") {
      const allowedTypes = ACTIVITY_TYPE_GROUPS[filteredType];
      if (allowedTypes) {
        filtered = filtered.filter(action => allowedTypes.includes(action.actionType));
      }
    }

    // Then filter by series if selected
    if (filteredSeries !== "all") {
      filtered = filtered.filter(action => {

        // Only include actions that have series information
        if (action.actionType === 'checklistUnlocked') {

          // For checklistUnlocked actions, we need to parse the series string
          // The series format is typically "2025 Topps Chrome® Baseball"
          // We need to extract: sport (MLB), year (2025), manufacturer (Topps), series (2025 Topps Chrome® Baseball)
          const seriesString = action.details?.series;

          if (seriesString) {
            // Extract year from the beginning of the series string
            const yearMatch = seriesString.match(/^(\d{4})/);
            const year = yearMatch ? yearMatch[1] : undefined;

            // Extract manufacturer by looking for common patterns in the series string
            // Instead of hardcoding, we'll look for the manufacturer after the year
            let manufacturer = undefined;
            if (year) {
              // Remove the year from the beginning and look for the first word/phrase
              const afterYear = seriesString.substring(4).trim();
              // Split by spaces and take the first part as manufacturer
              const firstPart = afterYear.split(' ')[0];
              if (firstPart && firstPart.length > 0) {
                manufacturer = firstPart;
              }
            }

            // For MLB cards, sport is typically MLB
            const sport = 'MLB';

            // Construct the series identifier to match against our filter
            const actionSeries = `${sport}_${year}_${manufacturer}_${seriesString}`;
            const matches = actionSeries === filteredSeries;
            return matches;
          } else {
            return false;
          }
        } else if (COLLECTION_CARD_ACTION_TYPES.includes(action.actionType)) {
          // Collection actions - check if they have cards from the selected series

          if (action.details?.cards) {
            // The cards structure is nested: sport -> year -> manufacturer -> series -> cardType -> category -> cardNumber
            // We need to check if any series in the nested structure matches our filter
            const hasMatchingSeries = Object.keys(action.details.cards).some(sportKey => {
              if (sportKey === "totalCardCount") return false;
              const sportData = action.details.cards[sportKey];

              return Object.keys(sportData).some(yearKey => {
                if (yearKey === "totalCardCount") return false;
                const yearData = sportData[yearKey];

                return Object.keys(yearData).some(manufacturerKey => {
                  if (manufacturerKey === "totalCardCount") return false;
                  const manufacturerData = yearData[manufacturerKey];

                  return Object.keys(manufacturerData).some(seriesKey => {
                    if (seriesKey === "totalCardCount") return false;

                    // Construct the series identifier to match against our filter
                    const actionSeries = `${sportKey}_${yearKey}_${manufacturerKey}_${seriesKey}`;
                    const matches = actionSeries === filteredSeries;

                    return matches;
                  });
                });
              });
            });

            return hasMatchingSeries;
          }
          return false;
        }

        // For other action types (account updates, token purchases, etc.), exclude them
        // when a specific series is selected since they're not series-specific
        return false;
      });

    }

    return filtered;
  }, [activityLog, filteredType, filteredSeries]);

  // Calculate total pages based on filtered results
  const totalPages = Math.ceil(filteredActivityLog.length / ITEMS_PER_PAGE);

  // Calculate pagination directly without useMemo to avoid dependency issues
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedActivityLog = filteredActivityLog.slice(startIndex, endIndex);

  // Fetch unlocked series data
  const fetchUnlockedSeries = useCallback(async () => {
    if (!user) return;

    try {
      const unlockDocRef = doc(db, `users/${user.uid}/unlockedChecklists`, 'allUnlocked');
      const unlockSnap = await getDoc(unlockDocRef);

      if (unlockSnap.exists()) {
        const unlockData = unlockSnap.data();

        const series = [];

        Object.values(unlockData).forEach(item => {
          if (item.isUnlocked && item.series) {
            // Create a readable series name
            const seriesName = `${item.year} ${item.manufacturer} ${item.series}`;
            const seriesValue = `${item.sport}_${item.year}_${item.manufacturer}_${item.series}`;


            series.push({
              value: seriesValue,
              label: seriesName
            });
          }
        });

        setUnlockedSeries(series);
      }
    } catch (error) {
    }
  }, [user]);

  // Fetch all data for the current filters
  const fetchAllData = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const activityLogRef = collection(db, `users/${user.uid}/activityLog`);

      // Fetch all data in batches
      let allDocs = [];
      let lastVisible = null;
      let hasMore = true;

      while (hasMore) {
        let q;
        if (!lastVisible) {
          q = query(activityLogRef, orderBy("timestamp", "desc"), limit(100));
        } else {
          q = query(activityLogRef, orderBy("timestamp", "desc"), startAfter(lastVisible), limit(100));
        }

        const querySnapshot = await getDocs(q);
        if (querySnapshot.empty) {
          hasMore = false;
          break;
        }

        const newDocs = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        allDocs = [...allDocs, ...newDocs];
        lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];

      }

      // Set all data to state
      setActivityLog(allDocs);

      // Reset to first page
      setCurrentPage(1);

    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Fetch specific page (now just updates the current page since we have all data)
  const fetchPage = useCallback(async (pageNumber) => {
    if (!user || pageNumber < 1) return;

    // If we don't have all data yet, fetch it first
    if (activityLog.length === 0) {
      await fetchAllData();
      return;
    }

    // Check if we have enough filtered data for this page
    const startIndex = (pageNumber - 1) * ITEMS_PER_PAGE;

    if (startIndex < filteredActivityLog.length) {
      setCurrentPage(pageNumber);
    } else {
      // Could implement infinite scroll here if needed
    }

  }, [user, activityLog.length, filteredActivityLog.length, fetchAllData]);

  // Reset pagination when filter changes
  useEffect(() => {
    if (user) {
      setCurrentPage(1);
      setPageCache({});
      pageCacheRef.current = {};
      fetchPage(1);
    }
  }, [filteredType, filteredSeries, user, fetchPage]);

  // Initial fetch
  useEffect(() => {
    if (user) {
      fetchUnlockedSeries();
      fetchAllData(); // Changed to fetchAllData
    }
  }, [user, fetchUnlockedSeries, fetchAllData]);

  // Handle page navigation
  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages && pageNumber !== currentPage) {
      fetchPage(pageNumber);
    }
  };


  const renderedActivityLog = useMemo(() => {
    return paginatedActivityLog.map((action) => {
      switch (action.actionType) {
        case "updatedTeam":
          return <TeamUpdatedEntry key={action.timestamp} content={action} />;
        case "updatedAccount":
          return <AccountUpdatedEntry key={action.timestamp} content={action} />;
        case "addedToCollection":
        case "removedFromCollection":
        case "soldFromCollection":
        case "returnedToCollection":
          return <CollectionUpdatedEntry key={action.timestamp} content={action} />;
        case "tokenPurchase":
          return <TokenPurchaseEntry key={action.timestamp} content={action} />;
        case "checklistUnlocked":
        case "promoCodeRedeemed":
        case "welcomeTokensGranted":
          return <TokenRedemptionEntry key={action.timestamp} content={action} />;
        default:
          return <h4 key={action.id} style={{ color: "#FFFFFF" }}>"Unaccounted for actionType"</h4>;
      }
    });
  }, [paginatedActivityLog]);

  // Handle filter change
  const handleFilterChange = (value) => {
    setFilteredType(value);
  };

  // Handle series filter change
  const handleSeriesFilterChange = (value) => {
    setFilteredSeries(value);
  };

  return (
    <ActivityLogContainer>
      <Title text={"ACTIVITY LOG"} />
      {!tokenOnly && (
        <>
          <FilterContainer>
            <InputDropdown id="activity-log-filter" onChange={handleFilterChange} value={filteredType}>
              <option value="all">All Activity</option>
              <option value="accountUpdated">Account Updated</option>
              <option value="addedToCollection">Added to Collection</option>
              <option value="removedFromCollection">Removed from Collection</option>
              <option value="sales">Sales</option>
              <option value="tokenPurchase">Token Purchase</option>
              <option value="tokenRedemption">Token Redemption</option>
            </InputDropdown>

            <InputDropdown id="series-filter" onChange={handleSeriesFilterChange} value={filteredSeries}>
              <option value="all">All Series</option>
              {unlockedSeries.map((series) => (
                <option key={series.value} value={series.value}>
                  {series.label}
                </option>
              ))}
            </InputDropdown>
          </FilterContainer>
        </>
      )}

      <ActivityLogDisplay>

        {paginatedActivityLog.length === 0 ? (
          <p style={{ color: "white" }}>No logs found for this filter.</p>
        ) : (
          <>
            {renderedActivityLog}

            <SystemPaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              isLoading={isLoading}
              onPageChange={goToPage}
            />
          </>
        )}
      </ActivityLogDisplay>
    </ActivityLogContainer>
  );
}